'use client';
import React, { FC, PropsWithChildren } from 'react';
import {
  AntdSchemaFormAdapters,
  AntdSchemaFormValidationAdapters,
} from '@sezenta/antd-schema-form-adapters';
import Form, { MapProps } from '@sezenta/antd-schema-form';
import { FileUpload } from './FileUpload/FileUpload';
import { EnumInput } from './EnumInput';
import { UploadInput } from './FileUpload/UploadInput';
import {
  InputNumber,
  TimePicker,
  ColorPicker,
  Select,
  Input,
  AutoComplete,
  Alert,
} from 'antd';
import { RichTextInput } from './RichText/RichTextInput';
import dayjs from 'dayjs';
import { Times } from './times';
import 'dayjs/locale/sv';
import 'dayjs/locale/si';
import 'dayjs/locale/fr-ch';
import { useTranslation } from 'react-i18next';
import { DaySelect } from './DaySelect';
import { StringListInput } from './StringListInput';
import { Utils } from './Utils';
import { VendorAddressSection } from '../WebUx/Vendors/VendorAddressSection';
import { VendorContactsSection } from '../WebUx/Vendors/VendorContactsSection';
import { TaxRatesInput } from '../WebUx/Taxes/TaxRatesInput';
import { VendorSelect } from '../WebUx/Vendors/VendorSelect';
import { SiteSelect } from '../WebUx/Sites/SiteSelect';
import { WarehouseSelect } from '../WebUx/Warehouses/WarehouseSelect';
import { ItemSelect } from '../WebUx/items/ItemSelect';
import { UserSelect } from './UserSelect';
import OptionedInput from './OptionedInput';
import EditableText from './EditableText';
import { CurrencyRateMessage } from './CurrencyRateMessage';
import PermissionSelect from './PermissionSelect';

export const FormConfigProvider: FC<PropsWithChildren> = (props) => {
  const { i18n } = useTranslation();
  const { language } = i18n;
  dayjs.locale(
    language === 'fr_FR'
      ? 'fr-ch'
      : language === 'sv_SE'
        ? 'sv'
        : language === 'si_LK'
          ? 'si'
          : 'en',
  );
  const times = Times.map((t) => ({
    label: dayjs(t, 'HH:mm').format(Utils.DEFAULT_TIME_FORMAT),
    value: t,
  }));
  return (
    <Form.ConfigProvider
      adapters={{
        ...AntdSchemaFormAdapters,
        select2: Select,
        file: FileUpload,
        enum: EnumInput,
        upload: UploadInput,
        permission: PermissionSelect,
        price: MapProps(InputNumber, {
          put: { style: { width: '100%' }, precision: 2 },
          encode: (value) =>
            value !== undefined ? Math.round(value * 100) : undefined,
          decode: (value) => (value !== undefined ? value / 100 : undefined),
        }),
        weight: MapProps(InputNumber, {
          encode: (value) =>
            value !== undefined ? Math.round(value * 1000) : undefined,
          decode: (value) => (value !== undefined ? value / 1000 : undefined),
          style: { width: '100%' },
        }),
        fourdecimal: MapProps(InputNumber, {
          put: { style: { width: '100%' }, precision: 4 },
          encode: (value) =>
            value !== undefined ? Math.round(value * 10000) : undefined,
          decode: (value) => (value !== undefined ? value / 10000 : undefined),
        }),
        cm: MapProps(InputNumber, {
          encode: (value) =>
            value !== undefined ? Math.round(value * 10) : undefined,
          decode: (value) => (value !== undefined ? value / 10 : undefined),
          style: { width: '100%' },
        }),
        richText: RichTextInput,
        timex: MapProps(TimePicker, {
          put: { format: 'HH:mm' },
          encode: (value: any) => value.format('HH:mm'),
          decode: (value: any) => dayjs(value, 'HH:mm'),
          style: { width: '100%' },
        }),
        colorPicker: MapProps(ColorPicker, {
          put: { disabledAlpha: true },
          encode: (value: any) => value.toHexString(),
          decode: (value: any) => value,
        }),
        timeselect: MapProps(Select, {
          put: { showSearch: true, options: times, optionFilterProp: 'label' },
        }),
        days: DaySelect,
        stringlist: StringListInput,
        autocomplete: AutoComplete,
        address: VendorAddressSection,
        contact: VendorContactsSection,
        taxrates: TaxRatesInput,
        vendor: VendorSelect,
        warehouse: WarehouseSelect,
        site: SiteSelect,
        item: ItemSelect,
        user: UserSelect,
        optionedInput: OptionedInput,
        info: Alert,
        editableText: EditableText,
        currencyRate: CurrencyRateMessage,
      }}
      validators={[...AntdSchemaFormValidationAdapters]}
    >
      {props.children as any}
    </Form.ConfigProvider>
  );
};
